import { useState } from 'react';
import { Button } from '@mui/material';
import { differenceInYears } from 'date-fns';

import { ButtonsBar, StyledTextField, StyledDatePicker, Panel, PanelHeader } from '@labradorsports/components';
import { isValidScreenName } from '@labradorsports/utils';

import { useDispatch } from '../../shared/hooks/index.js';
import { addValidation, useValidation } from '../../shared/index.js';
import { communityApi, useSetupCommunityProfileMutation } from '../../store/index.js';

interface Props {
    onSuccess: () => void;
    onCancel: () => void;
}

const CommunityProfileSetup: FC<Props> = ({ onSuccess, onCancel }) => {
    const dispatch = useDispatch();
    const [setupCommunityProfile] = useSetupCommunityProfileMutation();
    const [screenName, setScreenName] = useState('');
    const [birthdate, setBirthdate] = useState(null);

    const { errors, submitted, setSubmitted, validateRef } = useValidation(async () => {
        const result =
            screenName !== ''
                ? await dispatch(communityApi.endpoints.getCommunityProfile.initiate({ screenName }))
                : null;

        return {
            screenNameInvalid: !isValidScreenName(screenName),
            screenNameMissing: screenName === '',
            screenNameTaken: Boolean(result?.data),
            birthdate: differenceInYears(new Date(), birthdate) < 18,
        };
    }, [screenName, birthdate]);

    const screenNameChange = (evt) => {
        setScreenName(evt.target.value);
    };

    const onSubmit = async () => {
        if (await validateRef.current()) {
            await setupCommunityProfile({ screenName, description: '', birthdate });
            onSuccess();
        } else {
            setSubmitted(true);
        }
    };

    const screenNameError = (() => {
        if (errors.screenNameMissing) {
            return 'Please enter a screen name';
        }

        if (errors.screenNameTaken) {
            return 'Screen name is already taken';
        }

        if (errors.screenNameInvalid) {
            return 'Screen name is invalid';
        }

        return null;
    })();

    return (
        <Panel
            className="community-profile-setup"
            noPadding
            header={<PanelHeader title="Join Community" />}
            content={
                <div className="p-3">
                    Select a screen name:
                    <StyledTextField
                        label="Screen Name"
                        value={screenName}
                        onChange={screenNameChange}
                        helperText={submitted && screenNameError}
                        error={submitted && Boolean(screenNameError)}
                        fullWidth
                    />
                    <p className="small text-muted">Your screen name can be updated in your Profile Settings.</p>
                    <StyledDatePicker
                        label="Birthdate"
                        value={birthdate}
                        onChange={setBirthdate}
                        helperText={
                            submitted && errors.birthdate && 'Sorry, you must be at least 18 to join the community.'
                        }
                        error={submitted && errors.birthdate}
                        fullWidth
                    />
                    <p className="small text-muted">Must be at least 18 years old to join.</p>
                    <p>
                        Content posted to this community will be available to the public. All of your contributions to
                        this community will be listed under the Screen Name you select.
                    </p>
                    <p>
                        For additional information on rules of the forum, please review our{' '}
                        <a
                            href="https://app.termly.io/document/terms-of-use-for-saas/3e7f14b7-2cec-45f4-a500-d8c920b9036f"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Terms and Conditions
                        </a>
                    </p>
                </div>
            }
            footer={
                <ButtonsBar>
                    <Button className="float-start" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={onSubmit}>
                        Confirm
                    </Button>
                </ButtonsBar>
            }
        />
    );
};

export default addValidation(CommunityProfileSetup);
